import { OmniPickerRow } from "@/components/artifacts/omni/OmniPickerRow.tsx";
import { useCalendarColors } from "@/components/calendars/calendarColors.ts";
import {
  getCalendarProviderIcon,
  getCalendarProviderLabel,
} from "@/components/calendars/calendarProviderUtils.tsx";
import { useBusyCalendars } from "@/components/calendars/useBusyCalendars.ts";
import { busyCalendarSubtitle } from "@/shared/calendars/busyCalendarGroups.ts";
import { Loader } from "@/components/loader/Loader.tsx";
import { Badge } from "@/components/ui/badge.tsx";

// Subtitle wording lives in shared/client so native's rows read identically.
const getCalendarSubtitle = busyCalendarSubtitle;

interface CalendarSelectionProps {
  /**
   * The saved selected calendar IDs.
   */
  selectedCalendarIds: string | undefined;
}

/**
 * Calendar selection component for user preferences.
 * Allows users to select which calendars to use for availability checking.
 *
 * The list, the rules about which calendars are offered, and the autosave on
 * toggle all live in `useBusyCalendars` — this is the settings-screen rendering
 * of them. See `CalendarsOmniPickerBody` for the compact one.
 */
export const CalendarSelection = ({ selectedCalendarIds }: CalendarSelectionProps) => {
  const { groups, isLoading, hasMultipleConnectors, isPrimary, isSelected, toggle } =
    useBusyCalendars(selectedCalendarIds);
  const { getCalendarHex } = useCalendarColors();

  return (
    <div className="space-y-4 w-full">
      {isLoading ? (
        <div className="bg-white rounded-2xl">
          <div className="flex items-center justify-center py-8">
            <Loader size="sm" />
          </div>
        </div>
      ) : (
        groups.map((group, groupIndex) => (
          <div key={group.connectorId} className="bg-white rounded-2xl">
            <div className="flex items-center gap-2.5 px-3 md:px-4 py-3">
              {getCalendarProviderIcon(group.provider, "w-5 h-5")}
              <span className="text-sm font-medium text-zinc-900 truncate">
                {hasMultipleConnectors ? group.label : getCalendarProviderLabel(group.provider)}
              </span>
              {hasMultipleConnectors && (
                <Badge variant="tertiary" size="sm" className="ml-auto shrink-0">
                  {group.isLogin ? "Main account" : "Connector"}
                </Badge>
              )}
            </div>
            <div className="border-t-[0.5px] border-border" />
            <div className="py-1">
              {group.calendars.map((calendar) => {
                const isPrimaryAnywhere = isPrimary(calendar.calendarId);
                const subtitle = isPrimaryAnywhere
                  ? "Default calendar"
                  : getCalendarSubtitle(calendar);

                return (
                  // The same row the omnibox picker card uses, colored
                  // checkbox and all, so the three calendar surfaces read as
                  // one control.
                  <OmniPickerRow
                    key={`${calendar.accountId}/${calendar.calendarId}`}
                    checked={isSelected(calendar.calendarId)}
                    onToggle={() => void toggle(calendar.calendarId)}
                    locked={isPrimaryAnywhere}
                    label={calendar.summary || calendar.calendarId}
                    sublabel={subtitle}
                    checkboxColor={getCalendarHex(calendar.calendarId)}
                    // A calendar name can repeat across accounts; the group's
                    // account label keeps the accessible names apart.
                    ariaContext={
                      hasMultipleConnectors ? group.label : getCalendarProviderLabel(group.provider)
                    }
                    statsigName="settings-calendar-toggle"
                    className="px-3 md:px-4 py-3 hover:bg-secondary/50"
                  />
                );
              })}
            </div>
            {/* The note belongs to the list it explains, so it sits inside the
                card with it rather than loose underneath. On the artifact
                surfaces that boxed edge is what separates the agent's content
                from the panel around it, and a line of prose outside it read as
                chrome. Last group only: it describes the whole selection, not
                one connector. */}
            {groupIndex === groups.length - 1 && (
              <>
                <div className="border-t-[0.5px] border-border" />
                <p className="px-3 py-3 text-sm italic text-[#64646c] opacity-95 md:px-4">
                  Confirmed events will get booked on your default calendar. Default calendar cannot
                  be deselected.
                </p>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
};
